import { useState, useCallback, useEffect } from 'react';
import axios from 'axios';
import { convertFileToBase64 } from '../utils/tradeUtils';

export const useChatBot = () => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [imageBase64, setImageBase64] = useState('');

  // Load messages from localStorage on initial render
  useEffect(() => {
    try {
      const savedMessages = localStorage.getItem('chatMessages');
      if (savedMessages) {
        setMessages(JSON.parse(savedMessages));
      }
    } catch (err) {
      console.error('Error loading saved messages:', err);
    }
  }, []);

  // Save messages to localStorage when they change
  useEffect(() => {
    if (messages.length > 0) {
      localStorage.setItem('chatMessages', JSON.stringify(messages));
    }
  }, [messages]);

  const sendMessage = useCallback(async (message) => {
    if (!message.trim()) return;

    const userMessage = {
      id: Date.now(),
      text: message,
      sender: 'user',
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('idToken');
      const response = await axios.post(
        'https://v7jlylb599.execute-api.eu-west-1.amazonaws.com/aiChat',
        { message },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      const aiMessage = {
        id: Date.now() + 1,
        text: response.data.response,
        sender: 'ai',
        timestamp: new Date()
      };

      setMessages(prev => [...prev, aiMessage]);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to send message');
    } finally {
      setLoading(false);
    }
  }, []);

  const clearChat = useCallback(() => {
    setMessages([]);
    setError(null);
    localStorage.removeItem('chatMessages');
    setImageFile(null);
    setImagePreview('');
    setImageBase64('');
  }, []);

  const handleImageUpload = useCallback(async (file) => {
    if (!file) return;
    
    try {
      setImageFile(file);
      setImagePreview(URL.createObjectURL(file));
      const base64 = await convertFileToBase64(file);
      setImageBase64(base64);
      return true;
    } catch (err) {
      console.error('Error processing image:', err);
      setError('Failed to process image');
      return false;
    }
  }, []);

  const sendImageForAnalysis = useCallback(async (prompt = 'Analyze this chart and suggest a trade setup') => {
    if (!imageBase64) {
      setError('No image selected');
      return;
    }

    const userMessage = {
      id: Date.now(),
      text: prompt,
      sender: 'user',
      timestamp: new Date(),
      hasImage: true
    };

    setMessages(prev => [...prev, userMessage]);
    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('idToken');
      const response = await axios.post(
        'https://v7jlylb599.execute-api.eu-west-1.amazonaws.com/imagetojson_openai',
        { image_base64: imageBase64 },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      const extractedData = response.data.extractedTradeData || {};
      const analysisText = `Here's my analysis of the chart:\n\n` +
        `Symbol: ${extractedData.symbol || 'Not identified'}\n` +
        `Direction: ${extractedData.direction || 'Not identified'}\n` +
        `Entry Price: ${extractedData.entryPrice || 'Not identified'}\n` +
        `Stop Loss: ${extractedData.stopLoss || 'Not identified'}\n` +
        `Take Profit: ${extractedData.exitPrice || 'Not identified'}\n\n` +
        `Analysis: ${extractedData.insight || 'No specific insights available'}\n\n` +
        `Would you like me to create a trade based on this analysis?`;

      const aiMessage = {
        id: Date.now() + 1,
        text: analysisText,
        sender: 'ai',
        timestamp: new Date(),
        tradeData: extractedData
      };

      setMessages(prev => [...prev, aiMessage]);
      // Clear the image after analysis
      setImageFile(null);
      setImagePreview('');
      setImageBase64('');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to analyze image');
    } finally {
      setLoading(false);
    }
  }, [imageBase64]);

  const createTradeFromAnalysis = useCallback(async (tradeData) => {
    // Get settings for default values
    const savedSettings = localStorage.getItem('tradingSettings');
    const settings = savedSettings ? JSON.parse(savedSettings) : { defaultPositionSize: 10, defaultTags: [] };
    if (!tradeData) {
      setError('No trade data available');
      return;
    }

    setLoading(true);
    try {
      // Get current date and time
      const now = new Date();
      const currentDate = now.toISOString().split('T')[0];
      const currentTime = now.toTimeString().split(' ')[0].slice(0, 5);
      
      // Add default values
      const enhancedTradeData = {
        ...tradeData,
        category: tradeData.category || 'crypto',
        tradeDate: tradeData.tradeDate || currentDate,
        tradeTime: tradeData.tradeTime || currentTime,
        entryTime: currentTime,
        leverage: tradeData.leverage || '1',
        positionSize: tradeData.positionSize || settings.defaultPositionSize.toString() || '100',
        status: tradeData.status || 'OPEN',
        strategyIds: tradeData.strategyIds || [],
        tags: tradeData.tags || settings.defaultTags || [],
        rating: tradeData.rating || 3
      };
      
      // Calculate metrics
      const ep = parseFloat(enhancedTradeData.entryPrice);
      const sl = parseFloat(enhancedTradeData.stopLoss);
      const tp = parseFloat(enhancedTradeData.exitPrice);
      const size = parseFloat(enhancedTradeData.positionSize);
      const lev = parseFloat(enhancedTradeData.leverage);
      
      let metrics = {};
      
      if (ep && sl && tp && size && lev) {
        const risk = Math.abs(ep - sl);
        const reward = Math.abs(tp - ep);
        const qty = (size * lev) / ep;
        const riskAmt = risk * qty;
        const rewardAmt = reward * qty;
        
        metrics = {
          riskAmount: riskAmt.toFixed(2),
          rewardAmount: rewardAmt.toFixed(2),
          quantity: qty.toFixed(2),
          riskRewardRatio: (rewardAmt / riskAmt).toFixed(2)
        };
      }
      
      // Create final payload
      const payload = {
        ...enhancedTradeData,
        ...metrics,
        rrSummary: metrics.riskRewardRatio ? 
          `R:R = ${metrics.riskRewardRatio} | Risk: $${metrics.riskAmount} | Reward: $${metrics.rewardAmount} | Quantity: ${metrics.quantity}` : ''
      };
      
      const token = localStorage.getItem('idToken');
      await axios.post(
        'https://v7jlylb599.execute-api.eu-west-1.amazonaws.com/trades',
        payload,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      const aiMessage = {
        id: Date.now(),
        text: 'Trade created successfully! You can view it in your trade journal.',
        sender: 'ai',
        timestamp: new Date()
      };

      setMessages(prev => [...prev, aiMessage]);
      // Dispatch event to notify other components
      window.dispatchEvent(new Event('tradeCreated'));
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create trade');
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    messages,
    loading,
    error,
    imageFile,
    imagePreview,
    sendMessage,
    clearChat,
    handleImageUpload,
    sendImageForAnalysis,
    createTradeFromAnalysis
  };
};

export default useChatBot;